import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackbar } from '../../../../../store';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useStyles } from '../CustomFilter.style';
import {
  Typography,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Button,
  TableRow,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  CircularProgress,
  IconButton,
  useTheme,
  Tooltip,
  FormHelperText,
  Paper,
  Chip,
  TableSortLabel,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { formatDate } from '../../../../../helpers/formatDate';
import { formatPatientName } from '../../../../../helpers/misc';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import PeopleIcon from '@material-ui/icons/People';
import { fetchPost } from '../../../../../helpers/fetching';

const col = (width) => ({
  width: width,
  display: 'flex',
  flexDirection: 'column',
});

export default function CreateRandomList({ patientList, setPatientList, stats, setStats }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const institutionList = useSelector((state) => state.operators_devices.institution_list) || [];
  const operatorList = useSelector((state) => state.operators_devices.operator_list) || [];

  const uniqueOperatorList = useMemo(() => {
    const byName = new Map();
    (operatorList || []).forEach((op) => {
      const aliases = op.registered && op.aliases ? op.aliases : [op.name.toLowerCase()];
      const existing = byName.get(op.name);
      if (existing) {
        existing.aliases = [...new Set([...existing.aliases, ...aliases])];
      } else {
        byName.set(op.name, { name: op.name, registered: op.registered, aliases });
      }
    });
    return Array.from(byName.values());
  }, [operatorList]);

  const [timeRangeType, setTimeRangeType] = useState('preset');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [numberOfCasesType, setNumberOfCasesType] = useState('preset');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('ASC');
  const [orderBy, setOrderBy] = useState('');

  const patientListLoaded = patientList && patientList?.length > 0;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      numberOfCases: '',
      timeRange: '12months',
      postOp: false,
      operators: [],
      institutions: [],
    },
  });

  const tableHeader = [
    {
      name: 'studyId',
      label: t('Filters.examId'),
      align: 'left',
      padding: 'none',
      sortable: true,
    },
    { name: 'patientName', label: t('Filters.name'), align: 'left', padding: 'none', sortable: true },
    { name: 'dateOfBirth', label: t('Filters.dob'), align: 'left', padding: 'none', sortable: true },
    { name: 'examDate', label: t('Filters.date'), align: 'left', padding: 'none', sortable: true },
    {
      name: 'operatorName',
      label: t('Filters.operator'),
      align: 'left',
      padding: 'none',
      sortable: true,
    },
    {
      name: 'institution',
      label: t('Filters.institution'),
      align: 'left',
      padding: 'none',
      sortable: true,
    },
    { name: 'actions', label: '', align: 'left', padding: 'none', sortable: false },
  ];

  // Calculate preset dates
  const getPresetDates = (preset) => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setHours(23, 59, 59, 999);

    let startDate = new Date();
    switch (preset) {
      case '6months':
        startDate.setMonth(today.getMonth() - 6);
        break;
      case '12months':
        startDate.setMonth(today.getMonth() - 12);
        break;
      case '24months':
        startDate.setMonth(today.getMonth() - 24);
        break;
      default:
        startDate.setMonth(today.getMonth() - 12);
    }
    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  };

  // Handle time range preset change
  const handleTimeRangePresetChange = (preset) => {
    setValue('timeRange', preset);
    if (timeRangeType === 'preset') {
      const { startDate, endDate } = getPresetDates(preset);
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }
  };

  // Handle time range type change
  const handleTimeRangeTypeChange = (type) => {
    setTimeRangeType(type);
    if (type === 'preset') {
      const preset = watch('timeRange');
      const { startDate, endDate } = getPresetDates(preset);
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }
  };

  // Initialize dates on mount
  useEffect(() => {
    const preset = watch('timeRange');
    const { startDate, endDate } = getPresetDates(preset);
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  }, []);

  const onSubmit = async (data) => {
    // Validate custom dates if custom range is selected
    if (timeRangeType === 'custom') {
      if (!customStartDate || !customEndDate) {
        dispatch(
          setSnackbar({
            msg: 'Filters.custom_dates_required',
            severity: 'error',
          })
        );
        return;
      }
      if (customStartDate > customEndDate) {
        dispatch(
          setSnackbar({
            msg: 'Filters.start_date_before_end',
            severity: 'error',
          })
        );
        return;
      }
    }

    // Prepare form data with dates
    const { timeRange, ...restData } = data;
    const formData = {
      ...restData,
      startDate:
        timeRangeType === 'custom'
          ? formatDate(customStartDate, '00:00')
          : formatDate(getPresetDates(timeRange).startDate, '00:00'),
      endDate:
        timeRangeType === 'custom'
          ? formatDate(customEndDate, '23:59')
          : formatDate(getPresetDates(timeRange).endDate, '23:59'),
    };

    // Only include timeRange if not using custom dates
    if (timeRangeType !== 'custom') {
      formData.timeRange = timeRange;
    }

    setLoading(true);
    const response = await fetchPost('random_patient_list', formData, undefined, false);
    if (response.success) {
      setPatientList(response.data?.list);
      setStats(response.data?.stats);
      dispatch(
        setSnackbar({
          msg: 'Filters.filter_applied',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
    setLoading(false);
  };

  const removeUserFromList = (studyId) => {
    setPatientList((prevList) => prevList.filter((user) => user.studyId !== studyId));
    dispatch(
      setSnackbar({
        msg: 'Filters.patient_removed',
        severity: 'info',
        duration: 1000,
      })
    );
  };

  const getOperatorAliases = (operator) => {
    return operator.registered && operator.aliases ? operator.aliases : [operator.name.toLowerCase()];
  };

  const getSelectedOperatorNames = (selectedAliases) => {
    const selected = selectedAliases || [];
    return uniqueOperatorList
      .filter((op) => op.aliases.some((alias) => selected.includes(alias)))
      .map((op) => op.name);
  };

  // Sorting functions
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const formatStats = (stats) => {
    return `P: ${stats?.perfect?.toFixed(1)}% G: ${stats?.good?.toFixed(1)}% M: ${stats?.moderate?.toFixed(
      1
    )}% I: ${stats?.inadequate?.toFixed(1)}%`;
  };

  return (
    <div className={classes.search_container} style={{ padding: '0 20px' }}>
      <Typography variant='h6' className={classes.custom_filter_title}>
        {t('Filters.create_random_list_title')}
      </Typography>
      <Typography className={classes.custom_filter_description}>
        {t('Filters.create_random_list_helper_text')}
      </Typography>

      <div
        style={{
          display: 'flex',
          flexDirection: patientListLoaded ? 'row' : 'column',
          alignItems: patientListLoaded ? 'flex-start' : 'center',
          justifyContent: 'center',
          height: '100%',
          borderRadius: patientListLoaded ? '0 5px 5px 0' : '5px',
        }}>
        <div
          className={classes.filter_container}
          style={{
            width: patientListLoaded ? '30%' : '70%',
            borderRight: patientListLoaded ? '2px solid gray' : 'none',
            padding: '20px',
          }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <div style={{ width: '100%' }}>
              {/* First row */}
              <div className={patientListLoaded ? classes.column : classes.row}>
                <div
                  style={{
                    width: patientListLoaded ? '100%' : '50%',
                    display: 'flex',
                  }}>
                  <div style={{ display: 'flex', width: '100%', gap: '10px' }}>
                    {/* Number of Cases  */}
                    <div style={{ width: numberOfCasesType === 'other' ? '50%' : '100%' }}>
                      <FormControl variant='outlined' fullWidth className={classes.small_select}>
                        <InputLabel>{t('Filters.number_of_cases')}</InputLabel>
                        <Select
                          required
                          size='small'
                          value={numberOfCasesType === 'other' ? 'other' : watch('numberOfCases') || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'other') {
                              setNumberOfCasesType('other');
                              setValue('numberOfCases', '');
                            } else {
                              setNumberOfCasesType('preset');
                              setValue('numberOfCases', value);
                            }
                          }}
                          label={t('Filters.number_of_cases')}>
                          <MenuItem value='10'>10</MenuItem>
                          <MenuItem value='20'>20</MenuItem>
                          <MenuItem value='50'>50</MenuItem>
                          <MenuItem value='100'>100</MenuItem>
                          <MenuItem value='other'>{t('Filters.other')}</MenuItem>
                        </Select>
                      </FormControl>
                    </div>

                    {numberOfCasesType === 'other' && (
                      <div style={{ width: '50%' }}>
                        <Controller
                          name='numberOfCases'
                          control={control}
                          rules={{
                            required: t('Filters.number_of_cases_required'),
                            min: { value: 1, message: t('Filters.min_cases') },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              type='number'
                              size='small'
                              variant='outlined'
                              fullWidth
                              label={t('Filters.number_of_cases')}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              className={classes.small_text_field}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Post-op  */}
                <div style={{ width: patientListLoaded ? '100%' : '50%', paddingLeft: 8 }}>
                  <Controller
                    name='postOp'
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        style={{ padding: '0' }}
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color='primary'
                          />
                        }
                        label={
                          <Typography variant='caption' className={classes.form_helper_text}>
                            {t('Filters.include_post_operative')}
                          </Typography>
                        }
                      />
                    )}
                  />
                </div>
              </div>

              {/* Second row */}
              <div className={patientListLoaded ? classes.column : classes.row}>
                {/* Range Type */}
                <div style={patientListLoaded ? col('100%') : col('50%')}>
                  <FormControl variant='outlined' className={classes.small_select}>
                    <InputLabel>{t('Filters.range_type')}</InputLabel>
                    <Select
                      size='small'
                      value={timeRangeType}
                      onChange={(e) => handleTimeRangeTypeChange(e.target.value)}
                      label={t('Filters.range_type')}>
                      <MenuItem value='preset'>{t('Filters.predefined_range')}</MenuItem>
                      <MenuItem value='custom'>{t('Filters.custom_dates')}</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {/* PRESET RANGE */}
                {timeRangeType === 'preset' && (
                  <div style={patientListLoaded ? col('100%') : col('50%')}>
                    <Controller
                      name='timeRange'
                      control={control}
                      render={({ field }) => (
                        <FormControl variant='outlined' className={classes.small_select}>
                          <InputLabel>{t('Filters.select_range')}</InputLabel>
                          <Select
                            size='small'
                            {...field}
                            label={t('Filters.select_range')}
                            onChange={(e) => {
                              field.onChange(e);
                              handleTimeRangePresetChange(e.target.value);
                            }}>
                            <MenuItem value='6months'>{t('Filters.last_6_months')}</MenuItem>
                            <MenuItem value='12months'>{t('Filters.last_12_months')}</MenuItem>
                            <MenuItem value='24months'>{t('Filters.last_24_months')}</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                )}

                {/* CUSTOM RANGE */}
                {timeRangeType === 'custom' && (
                  <div style={{ display: 'flex', width: patientListLoaded ? '100%' : '50%', gap: '10px' }}>
                    <div style={{ ...col('50%') }}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                          fullWidth
                          size='small'
                          label={t('Filters.start_date')}
                          value={customStartDate}
                          onChange={(date) => setCustomStartDate(date)}
                          format='dd/MM/yyyy'
                          inputVariant='outlined'
                          maxDate={customEndDate || new Date()}
                          className={classes.small_date_picker}
                          style={{ minWidth: '90px' }}
                        />
                      </MuiPickersUtilsProvider>
                    </div>

                    <div style={{ ...col('50%') }}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                          fullWidth
                          size='small'
                          label={t('Filters.end_date')}
                          value={customEndDate}
                          onChange={(date) => setCustomEndDate(date)}
                          format='dd/MM/yyyy'
                          inputVariant='outlined'
                          minDate={customStartDate}
                          maxDate={new Date()}
                          className={classes.small_date_picker}
                          style={{ minWidth: '90px' }}
                        />
                      </MuiPickersUtilsProvider>
                    </div>
                  </div>
                )}
              </div>

              {/* Third row */}
              <div className={patientListLoaded ? classes.column : classes.row}>
                {/* Institutions */}
                <div style={patientListLoaded ? col('100%') : col('50%')}>
                  <Controller
                    name='institutions'
                    control={control}
                    render={({ field }) => (
                      <FormControl variant='outlined' className={classes.small_select} fullWidth>
                        <Typography variant='caption' className={classes.form_helper_text}>
                          {t('Filters.institutions')}
                        </Typography>
                        <Select
                          size='small'
                          multiple
                          displayEmpty
                          {...field}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <em style={{ color: 'rgba(255, 255, 255, 0.54)' }}>
                                {t('Filters.all_institutions_label')}
                              </em>
                            ) : (
                              selected.join(', ')
                            )
                          }>
                          {institutionList.map((inst, i) => (
                            <MenuItem key={i} value={inst.institution}>
                              <Checkbox checked={field.value?.includes(inst.institution) || false} />
                              {inst.institution}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{t('Filters.all_institutions_helper')}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </div>

                {/* Operators */}
                <div style={patientListLoaded ? col('100%') : col('50%')}>
                  <Controller
                    name='operators'
                    control={control}
                    render={({ field }) => {
                      const selectedOperatorNames = getSelectedOperatorNames(field.value || []);

                      const handleOperatorChange = (event) => {
                        const selectedNames = event.target.value;
                        let newAliases = [];
                        selectedNames.forEach((name) => {
                          const op = uniqueOperatorList.find((o) => o.name === name);
                          if (op) {
                            newAliases = [...newAliases, ...op.aliases];
                          }
                        });
                        field.onChange([...new Set(newAliases)]);
                      };

                      return (
                        <FormControl variant='outlined' className={classes.small_select} fullWidth>
                          <Typography variant='caption' className={classes.form_helper_text}>
                            {t('Filters.operators')}
                          </Typography>
                          <Select
                            size='small'
                            multiple
                            displayEmpty
                            value={selectedOperatorNames}
                            onChange={handleOperatorChange}
                            renderValue={(selected) =>
                              selected.length === 0 ? (
                                <em style={{ color: 'rgba(255, 255, 255, 0.54)' }}>
                                  {t('Filters.all_operators_label')}
                                </em>
                              ) : (
                                selected.join(', ')
                              )
                            }>
                            {uniqueOperatorList.map((op) => (
                              <MenuItem key={op.name} value={op.name}>
                                <Checkbox checked={selectedOperatorNames.includes(op.name)} />
                                {op.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{t('Filters.all_operators_helper')}</FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {patientList && patientList.length > 0 && (
                <Button
                  type='button'
                  variant='outlined'
                  color='secondary'
                  size='small'
                  className={classes.clear_action_button}
                  onClick={() => setPatientList([])}>
                  {t('Filters.clear_list')}
                </Button>
              )}

              <Button
                type='submit'
                variant='contained'
                color='secondary'
                size='small'
                className={classes.action_button}>
                {patientList && patientList.length > 0
                  ? t('Filters.regenerate_list')
                  : t('Filters.generate_list')}
              </Button>
            </div>
          </form>
        </div>

        {/* Table with patient list retrieved from the API */}
        <div
          className={classes.filter_container}
          style={{
            width: patientListLoaded ? '70%' : '100%',
            display: patientListLoaded ? 'initial' : 'none',
            minHeight: '100%',
          }}>
          <div className={classes.table_title_container}>
            <Typography className={classes.table_title}>{t('Filters.custom_list_title')}</Typography>
            <Typography className={classes.table_stats}>{formatStats(stats)}</Typography>
            <div className={classes.table_title_container}>
              <Tooltip
                arrow
                placement='top-end'
                title={<Typography className={classes.tooltip_text}>{t('Filters.number_added')}</Typography>}>
                <Chip
                  label={`${patientList?.length}`}
                  variant='outlined'
                  size='small'
                  color='secondary'
                  icon={<PeopleIcon style={{ margin: '4px' }} />}
                />
              </Tooltip>
            </div>
          </div>
          <TableContainer component={Paper} className={classes.table_container}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeader.map((head, index) => (
                    <TableCell
                      key={head.label || index}
                      align={head.align || 'left'}
                      className={classes.table_row}
                      sortDirection={orderBy === head.name ? order.toLowerCase() : false}>
                      {head.sortable ? (
                        <TableSortLabel
                          active={orderBy === head.name}
                          direction={orderBy === head.name ? order.toLowerCase() : 'asc'}
                          onClick={createSortHandler(head.name)}>
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align='center' colSpan={tableHeader.length}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : patientList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      align={'center'}
                      colSpan={tableHeader.length}
                      variant='footer'
                      style={{ borderBottom: 'unset' }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}>
                        <Typography
                          className={classes.table_placeholder}
                          style={{ fontSize: theme.fonts.responsiveMediumBig }}>
                          {t('Filters.placeholder_empty')}
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  [...patientList].sort(getComparator(order, orderBy)).map((user) => (
                    <TableRow key={user.studyId}>
                      <TableCell
                        className={classes.table_row}
                        style={{ fontSize: theme.fonts.responsiveSmall, paddingRight: '10px' }}>
                        {user.studyId}
                      </TableCell>
                      <TableCell className={classes.table_row}>
                        {formatPatientName(user.patientName) || formatPatientName(user.name)}
                      </TableCell>
                      <TableCell className={classes.table_row}>{user.dateOfBirth}</TableCell>
                      <TableCell className={classes.table_row}>{user.examDate}</TableCell>
                      <TableCell className={classes.table_row}>{user.operatorName}</TableCell>
                      <TableCell className={classes.table_row}>{user.institution || '-'}</TableCell>
                      <TableCell className={classes.table_row}>
                        <Tooltip
                          title={
                            <Typography className={classes.tooltip_text}>{t('Filters.remove')}</Typography>
                          }
                          arrow
                          placement='top-start'>
                          <IconButton onClick={() => removeUserFromList(user.studyId)} size='small'>
                            <HighlightOffIcon fontSize='small' color='secondary' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}
